import {pannellumOptions} from '../options/pannellum.options';

export const pannellumDirective = {
    options: pannellumOptions,

    init(element, options) {
        const {panorama, hotSpots, ...config} = options;

        if (panorama) {
            config.panorama = panorama;
        }

        if (typeof hotSpots === 'string' && hotSpots) {
            fetch(hotSpots)
                .then((response) => response.json())
                .then((data) => {
                    let spots = [];

                    if (Array.isArray(data)) {
                        spots = data;
                    } else if (data && typeof data === 'object') {
                        if (data.panorama) {
                            config.panorama = data.panorama;
                        }
                        if (data.autoRotate !== undefined) {
                            config.autoRotate = data.autoRotate;
                        }
                        spots = data.hotSpots || [];
                    }

                    if (spots.length) {
                        config.hotSpots = this.transformHotSpots(spots);
                    }

                    this.createViewer(element, config);
                })
                .catch(() => {
                    if (config.panorama) {
                        this.createViewer(element, config);
                    }
                });
        } else if (config.panorama) {
            this.createViewer(element, config);
        }
    },

    transformHotSpots(spots) {
        return spots.map((spot) => ({
            id: spot.id,
            pitch: spot.pitch,
            yaw: spot.yaw,
            cssClass: 'sirv-hotspot-pointer',
            createTooltipFunc: (hotSpotDiv, args) => {
                hotSpotDiv.style.cursor = 'pointer';
                hotSpotDiv.addEventListener('click', (e) => {
                    e.stopPropagation();
                    this.openPopup(args.text);
                });
            },
            createTooltipArgs: {text: spot.text}
        }));
    },

    openPopup(content) {
        this.closePopup();

        const overlay = document.createElement('div');
        overlay.classList.add('sirv-hotspot-box');
        overlay.style.display = 'flex';
        overlay.style.zIndex = '9999';
        overlay.dataset.dragClose = 'hide';
        overlay.dataset.dragCloseTarget = '.sirv-hotspot-box-wrapper';
        overlay.dataset.dragCloseScrollable = '.sirv-hotspot-box-content';

        const wrapper = document.createElement('div');
        wrapper.classList.add('sirv-hotspot-box-wrapper');
        wrapper._popupDragInit = true;

        const closeBtn = document.createElement('div');
        closeBtn.classList.add('sirv-hotspot-close-button');
        closeBtn.addEventListener('click', () => this.closePopup());

        const contentEl = document.createElement('div');
        contentEl.classList.add('sirv-hotspot-box-content');
        contentEl.innerHTML = content;

        contentEl.querySelectorAll('[data-dismiss="modal"]').forEach((btn) => {
            btn.addEventListener('click', () => this.closePopup());
        });

        wrapper.appendChild(closeBtn);
        wrapper.appendChild(contentEl);
        overlay.appendChild(wrapper);

        overlay.addEventListener('click', (e) => {
            if (e.target === overlay) {
                this.closePopup();
            }
        });

        this._escapeHandler = (e) => {
            if (e.key === 'Escape') {
                this.closePopup();
            }
        };
        document.addEventListener('keydown', this._escapeHandler);

        document.body.appendChild(overlay);
    },

    closePopup() {
        const existing = document.querySelector('.sirv-hotspot-box');
        if (existing) {
            existing.remove();
        }

        if (this._escapeHandler) {
            document.removeEventListener('keydown', this._escapeHandler);
            this._escapeHandler = null;
        }
    },

    createViewer(element, config) {
        if (typeof pannellum !== 'undefined') {
            element._pannellumViewer = pannellum.viewer(element, config);
            return;
        }

        // CDN script loaded with defer may not be ready yet — poll until available
        const poll = setInterval(() => {
            if (typeof pannellum !== 'undefined') {
                clearInterval(poll);
                element._pannellumViewer = pannellum.viewer(element, config);
            }
        }, 100);

        // Stop polling after 10s to avoid leaking intervals
        setTimeout(() => clearInterval(poll), 10000);
    },

    destroy(element) {
        this.disableRotatedDrag(element);
        this.closePopup();
        if (element._pannellumViewer) {
            element._pannellumViewer.destroy();
            element._pannellumViewer = null;
        }
    },

    /**
     * Enable rotated drag mode for a CSS-rotated Pannellum viewer.
     *
     * When the Pannellum container is rotated 90deg via CSS transform, the browser's
     * touch/mouse coordinate system no longer matches the panorama axes. This intercepts
     * touch and mouse events (capture phase) before Pannellum sees them and maps
     * screen deltas to yaw/pitch with swapped axes.
     *
     * @param {HTMLElement} element Pannellum HTML element
     */
    enableRotatedDrag(element) {
        const viewer = element._pannellumViewer;
        if (!viewer || element._rotatedDrag) return;

        let isDragging = false;
        let startX = 0;
        let startY = 0;
        let startYaw = 0;
        let startPitch = 0;
        let speed = 0;

        const beginDrag = (clientX, clientY) => {
            isDragging = true;
            startX = clientX;
            startY = clientY;
            startYaw = viewer.getYaw();
            startPitch = viewer.getPitch();
            speed = viewer.getHfov() / element.clientWidth;
            viewer.stopAutoRotate();
            viewer.stopMovement();
        };

        const updateDrag = (clientX, clientY) => {
            if (!isDragging) return;
            const deltaX = clientX - startX;
            const deltaY = clientY - startY;

            // 90deg CW rotation: screen X+ → container Y+ (down), screen Y+ → container X- (left)
            viewer.setYaw(startYaw - deltaY * speed, false);
            viewer.setPitch(startPitch + deltaX * speed, false);
        };

        const endDrag = () => {
            isDragging = false;
        };

        const onTouchStart = (e) => {
            e.stopImmediatePropagation();
            if (e.touches.length === 1) {
                beginDrag(e.touches[0].clientX, e.touches[0].clientY);
            }
        };

        const onTouchMove = (e) => {
            e.stopImmediatePropagation();
            e.preventDefault();
            if (e.touches.length === 1) {
                updateDrag(e.touches[0].clientX, e.touches[0].clientY);
            }
        };

        const onTouchEnd = (e) => {
            e.stopImmediatePropagation();
            endDrag();
        };

        const onMouseDown = (e) => {
            if (e.button !== 0) return;
            e.stopImmediatePropagation();
            beginDrag(e.clientX, e.clientY);
            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('mouseup', onMouseUp);
        };

        const onMouseMove = (e) => {
            updateDrag(e.clientX, e.clientY);
        };

        const onMouseUp = () => {
            endDrag();
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };

        element.addEventListener('touchstart', onTouchStart, {capture: true});
        element.addEventListener('touchmove', onTouchMove, {capture: true});
        element.addEventListener('touchend', onTouchEnd, {capture: true});
        element.addEventListener('touchcancel', onTouchEnd, {capture: true});
        element.addEventListener('mousedown', onMouseDown, {capture: true});

        element._rotatedDrag = {onTouchStart, onTouchMove, onTouchEnd, onMouseDown, onMouseMove, onMouseUp};
    },

    /**
     * Disable rotated drag mode and restore Pannellum's built-in drag.
     *
     * @param {HTMLElement} element Pannellum HTML element
     */
    disableRotatedDrag(element) {
        const state = element._rotatedDrag;
        if (!state) return;

        element.removeEventListener('touchstart', state.onTouchStart, {capture: true});
        element.removeEventListener('touchmove', state.onTouchMove, {capture: true});
        element.removeEventListener('touchend', state.onTouchEnd, {capture: true});
        element.removeEventListener('touchcancel', state.onTouchEnd, {capture: true});
        element.removeEventListener('mousedown', state.onMouseDown, {capture: true});
        document.removeEventListener('mousemove', state.onMouseMove);
        document.removeEventListener('mouseup', state.onMouseUp);

        delete element._rotatedDrag;
    }
};
